import { Box } from '@mui/material';
import { useState, useEffect, useRef } from 'react';

import { Interest } from './Interest';
import { INTERESTS, ROLES } from '../IntroductionModal/introductionSteps.data';
import type { TDashboardView } from '../../utils/dashboardView';

export type TOnChangePayload = {
    user_type: string[];
    interest: string[];
    /** Set during signup when user picks Brand vs Creator workspace */
    dashboard_view?: TDashboardView;
};

interface IInterests {
    onChange: (val: TOnChangePayload) => void;
    initialValue?: {
        user_type: string[];
        interest: string[];
    };
}

const Interests: React.FC<IInterests> = ({ onChange, initialValue }) => {
    const [selections, setSelections] = useState<TOnChangePayload>({
        interest: initialValue?.interest || [],
        user_type: initialValue?.user_type || [],
    });
    const prevInitialValueRef = useRef<{ user_type: string[]; interest: string[] } | undefined>(initialValue);

    // Helper to compare arrays by content
    const arraysEqual = (a: string[], b: string[]) => {
        if (a.length !== b.length) return false;
        const aSet = new Set(a);
        const bSet = new Set(b);
        return a.every(val => bSet.has(val)) && b.every(val => aSet.has(val));
    };

    // Sync selections when initialValue changes (only when it actually changes in content)
    useEffect(() => {
        const prevInitial = prevInitialValueRef.current;

        // Check if initialValue actually changed in content (not just reference)
        const initialValueChanged =
            (prevInitial === undefined && initialValue !== undefined) ||
            (prevInitial !== undefined && initialValue === undefined) ||
            (prevInitial &&
                initialValue &&
                (!arraysEqual(prevInitial.interest, initialValue.interest) ||
                    !arraysEqual(prevInitial.user_type, initialValue.user_type)));

        if (initialValueChanged) {
            const updated = {
                interest: initialValue?.interest || [],
                user_type: initialValue?.user_type || [],
            };
            setSelections(updated);
            prevInitialValueRef.current = initialValue;
        }
    }, [initialValue]);

    const handleInterestChange = (selected: string[]) => {
        const updated = { ...selections, interest: selected };
        setSelections(updated);
        onChange(updated);
    };

    const handleRoleChange = (selected: string[]) => {
        const updated = { ...selections, user_type: selected };
        setSelections(updated);
        onChange(updated);
    };

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '600px',
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
            }}>
            <Interest
                header="What Interests You?"
                options={INTERESTS}
                onChange={handleInterestChange}
                initialValue={initialValue?.interest}
            />
            <Interest
                header="How Can we address You?"
                options={ROLES}
                sx={{ marginBottom: 2 }}
                onChange={handleRoleChange}
                initialValue={initialValue?.user_type}
            />
        </Box>
    );
};

export { Interests };
